import { useEffect, useState, type ReactNode } from "react";
import { Link, useLocation } from "react-router";
import { Menu, X } from "lucide-react";
import { BookrLogo } from "@/design/BookrLogo";
import { ICONS, type NavItem } from "@/design/nav";

/**
 * Primary navigation for both signed-in surfaces (design system §4 chrome),
 * ported in R22 T12.
 *
 * **The sections themselves live in `@/design/navItems`**, shared with the Next
 * host, so the information architecture cannot drift between the two while both
 * are serving. What is here is the markup, which differs only because each
 * frontend has its own router.
 *
 * Sections are the top level of the IA and nothing else lives here: customers
 * get Search / Bookings / Settings, businesses get Schedule / Dashboard /
 * Settings, and everything else is reached from inside a section.
 *
 * Layout: horizontal links from `md` up, a hamburger drawer below it. The
 * drawer closes on route change, on Escape, and locks background scroll while
 * open so the page behind cannot be scrolled by accident.
 */
export function AppNav({
  items,
  homeHref = "/",
  trailing,
  children,
}: {
  items: NavItem[];
  homeHref?: string;
  /**
   * Controls that stay visible at every width, next to the menu button. The
   * notification bell lives here: an unread count that only appears once you
   * open the drawer is a count nobody sees on a phone.
   */
  trailing?: ReactNode;
  /**
   * Secondary controls (theme toggle, log out). Inline from `md` up and in the
   * drawer footer below it, because neither is urgent enough to spend header
   * width on a phone.
   */
  children?: ReactNode;
}) {
  const { pathname } = useLocation();

  // The drawer remembers the route it was opened on, so navigating away closes
  // it by derivation rather than by syncing state in an effect.
  const [openedOn, setOpenedOn] = useState<string | null>(null);
  const open = openedOn !== null && openedOn === pathname;
  const setOpen = (next: boolean) => setOpenedOn(next ? pathname : null);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpenedOn(null);
    };
    document.addEventListener("keydown", onKey);
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = previous;
    };
  }, [open]);

  const isActive = (href: string) =>
    pathname === href || (href !== "/" && pathname.startsWith(`${href}/`));

  return (
    <>
      {/* `print:hidden`: a printed page is the content alone (the meal plan,
          H53, is the screen people print). */}
      <header className="sticky top-0 z-40 border-b border-line bg-surface/95 backdrop-blur print:hidden">
        <div className="mx-auto flex h-16 w-full max-w-6xl items-center gap-4 px-4 sm:px-5">
          {/* Menu sits on the left, before the brand, so it lands under the
              thumb on a phone and matches where a drawer trigger is expected. */}
          <button
            type="button"
            onClick={() => setOpen(true)}
            aria-label="Open menu"
            aria-expanded={open}
            className="-ms-1 inline-flex size-10 shrink-0 items-center justify-center rounded-lg text-ink transition-colors hover:bg-page md:hidden"
          >
            <Menu className="size-6" aria-hidden />
          </button>

          <Link to={homeHref} aria-label="Bookr home" className="shrink-0">
            <BookrLogo size={26} />
          </Link>

          <nav className="ms-4 hidden items-center gap-1 md:flex">
            {items.map((item) => {
              const Icon = ICONS[item.icon];
              const active = isActive(item.href);
              return (
                <Link
                  key={item.href}
                  to={item.href}
                  aria-current={active ? "page" : undefined}
                  className={`inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold transition-colors ${
                    active ? "bg-accent/12 text-accent" : "text-muted hover:text-ink"
                  }`}
                >
                  <Icon className="size-4" aria-hidden />
                  {item.label}
                  {item.badge ? <Badge count={item.badge} /> : null}
                </Link>
              );
            })}
          </nav>

          <div className="ms-auto flex items-center gap-1 sm:gap-2">
            {trailing}
            <div className="hidden items-center gap-2 md:flex">{children}</div>
          </div>
        </div>
      </header>

      {/* Phone drawer.
          A sibling of <header>, NOT a child: the header uses backdrop-blur, and
          a backdrop-filter makes an element a containing block for fixed
          descendants, which would size this drawer to the header instead of the
          viewport (design-system.md §12). */}
      {open && (
        <div className="fixed inset-0 z-50 md:hidden">
          <button
            type="button"
            aria-label="Close menu"
            onClick={() => setOpen(false)}
            className="absolute inset-0 bg-ink/40 backdrop-blur-[2px]"
          />
          <div className="absolute inset-y-0 start-0 flex w-[min(20rem,85vw)] flex-col bg-surface shadow-[var(--shadow-lift)]">
            <div className="flex h-16 shrink-0 items-center justify-between border-b border-line px-4">
              <BookrLogo size={24} />
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Close menu"
                className="inline-flex size-10 items-center justify-center rounded-lg border border-line text-ink transition-colors hover:bg-page"
              >
                <X className="size-5" aria-hidden />
              </button>
            </div>

            <nav className="flex-1 overflow-y-auto p-3">
              <ul className="space-y-1">
                {items.map((item) => {
                  const Icon = ICONS[item.icon];
                  const active = isActive(item.href);
                  return (
                    <li key={item.href}>
                      <Link
                        to={item.href}
                        onClick={() => setOpen(false)}
                        className={`flex w-full items-center gap-3 rounded-lg px-3 py-3 text-start text-base font-semibold transition-colors ${
                          active
                            ? "bg-accent/12 text-accent"
                            : "text-body hover:bg-page hover:text-ink"
                        }`}
                      >
                        <Icon className="size-5 shrink-0" aria-hidden />
                        {item.label}
                        {item.badge ? <Badge count={item.badge} /> : null}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </nav>

            <div className="flex shrink-0 flex-wrap items-center gap-2 border-t border-line p-3">
              {children}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function Badge({ count }: { count: number }) {
  return (
    <span className="ms-0.5 inline-flex min-w-5 items-center justify-center rounded-md bg-accent px-1.5 py-0.5 font-mono text-[11px] font-bold text-on-accent">
      {count}
    </span>
  );
}
